import { useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  Download,
  FileSpreadsheet,
  Users,
  Target,
  FileCheck,
  Loader2,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ExportPage() {
  const [exportingSellers, setExportingSellers] = useState(false);
  const [exportingObjectives, setExportingObjectives] = useState(false);
  const [exportingHistory, setExportingHistory] = useState(false);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  
  const monthNamesFull = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const handleExportSellers = async () => {
    setExportingSellers(true);
    try {
      const response = await api.get("/export/sellers", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendeurs_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export des vendeurs téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingSellers(false);
    }
  };

  const handleExportObjectives = async () => {
    setExportingObjectives(true);
    try {
      const response = await api.get("/export/objectives", { 
        params: { year: parseInt(selectedYear), month: parseInt(selectedMonth) },
        responseType: 'blob' 
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `objectifs_${monthNamesFull[parseInt(selectedMonth)-1]}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export des objectifs téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingObjectives(false);
    }
  };

  const handleExportHistory = async () => {
    setExportingHistory(true);
    try {
      const response = await api.get("/export/history", { 
        params: { year: parseInt(selectedYear) },
        responseType: 'blob' 
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historique_signatures_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export de l'historique téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingHistory(false);
    }
  };

  return (
    <Layout title="Exports Excel">
      <div className="max-w-4xl">
        {/* Period selector */}
        <div className="bg-white rounded-xl border p-6 mb-6" data-testid="period-selector">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Période de référence
              </h2>
              <p className="text-sm text-gray-500">Sélectionnez la période pour les exports</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32" data-testid="export-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2027">2027</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40" data-testid="export-month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNamesFull.map((month, idx) => (
                  <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Export cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sellers export */}
          <div className="bg-white rounded-xl border p-6 hover:shadow-lg transition-shadow" data-testid="export-sellers-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-3 rounded-xl">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-[#212E38]">Vendeurs</h3>
                <p className="text-xs text-gray-500">Liste complète</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Export de tous les vendeurs actifs avec leurs informations (nom, email, marques, concession).
            </p>
            
            <Button
              onClick={handleExportSellers}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={exportingSellers}
              data-testid="export-sellers-btn"
            >
              {exportingSellers ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Télécharger
            </Button>
          </div>

          {/* Objectives export */}
          <div className="bg-white rounded-xl border p-6 hover:shadow-lg transition-shadow" data-testid="export-objectives-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-xl">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-[#212E38]">Objectifs</h3>
                <p className="text-xs text-gray-500">{monthNamesFull[parseInt(selectedMonth)-1]} {selectedYear}</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Export des objectifs du mois sélectionné (VN, VO, DFS, statut de signature).
            </p>
            
            <Button
              onClick={handleExportObjectives}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={exportingObjectives}
              data-testid="export-objectives-btn"
            >
              {exportingObjectives ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Télécharger
            </Button>
          </div>

          {/* History export */}
          <div className="bg-white rounded-xl border p-6 hover:shadow-lg transition-shadow" data-testid="export-history-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-100 p-3 rounded-xl">
                <FileCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-[#212E38]">Historique signatures</h3>
                <p className="text-xs text-gray-500">Année {selectedYear}</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Export de tous les documents signés pour l'année sélectionnée.
            </p>
            
            <Button
              onClick={handleExportHistory}
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={exportingHistory}
              data-testid="export-history-btn"
            >
              {exportingHistory ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Télécharger
            </Button>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-600">
                Les fichiers sont exportés au format <strong>Excel (.xlsx)</strong> et peuvent être ouverts avec Microsoft Excel, Google Sheets ou LibreOffice Calc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
